<template>
  <div class="fts-settings-page">
    <header class="fts-settings-head">
      <div>
        <h1>Security</h1>
        <p>How people sign in, who can join, and what members can see.</p>
      </div>
    </header>

    <section class="fts-settings-card">
      <div>
        <h2>Sign-in</h2>
      </div>

      <div class="fts-settings-loading" v-if="twoFactor.loading">
        <fts-spinner :size="16" />
        <span>Loading&hellip;</span>
      </div>

      <!-- An environment whose API predates this endpoint says so rather than drawing a
           switch that cannot know its own position. -->
      <div class="fts-settings-row" v-else-if="twoFactor.unavailable">
        <span class="fts-settings-row-text">
          <strong>Require two-step verification</strong>
          <span>{{ twoFactor.unavailable }}</span>
        </span>
      </div>

      <template v-else>
        <div class="fts-settings-row">
          <span class="fts-settings-row-text">
            <strong>Require two-step verification</strong>
            <span>
              Everyone signs in with an authenticator app as well as a password. Anyone who has not
              set one up is walked through it at their next sign-in.
            </span>
          </span>
          <button
            type="button"
            role="switch"
            class="fts-settings-switch"
            :aria-checked="String(twoFactor.enforced)"
            aria-label="Require two-step verification"
            :disabled="twoFactor.saving"
            @click="toggleTwoFactor"
          >
            <span></span>
          </button>
        </div>

        <div class="fts-settings-enrolment" v-if="twoFactor.total">
          <div class="fts-settings-enrolment-bar">
            <div class="fts-settings-enrolment-text">
              <span>{{ enrolmentLine }}</span>
              <span>{{ enrolmentPct }}</span>
            </div>
            <div class="fts-settings-meter">
              <div class="fts-settings-meter-fill" :style="{ width: enrolmentPct }"></div>
            </div>
          </div>
        </div>

        <span class="fts-settings-note" v-if="twoFactor.enforced && twoFactor.pending">
          The {{ twoFactor.pending }} who have not set it up keep working as usual until their next
          sign-in, where they are walked through it.
        </span>
      </template>
    </section>

    <section class="fts-settings-card">
      <div>
        <h2>What members can see</h2>
        <p class="fts-settings-card-sub">
          By default any member can switch the dashboard to everyone&rsquo;s activity and add a
          calendar for everyone. Turn a restriction on to keep that for workspace admins.
        </p>
      </div>

      <div class="fts-settings-loading" v-if="loading">
        <fts-spinner :size="16" />
        <span>Loading&hellip;</span>
      </div>

      <div class="fts-settings-banner is-warn" v-else-if="error">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <span>{{ error }}</span>
      </div>

      <template v-else>
        <div class="fts-settings-row">
          <span class="fts-settings-row-text">
            <strong>Only admins see everyone&rsquo;s activity stream</strong>
            <span>Other members see their own activity.</span>
          </span>
          <button
            type="button"
            role="switch"
            class="fts-settings-switch"
            :aria-checked="String(access.restrictActivityStream)"
            aria-label="Only admins see everyone's activity stream"
            :disabled="saving"
            @click="toggle('restrictActivityStream')"
          >
            <span></span>
          </button>
        </div>

        <div class="fts-settings-row">
          <span class="fts-settings-row-text">
            <strong>Only admins add a calendar for everyone</strong>
            <span>Other members add calendars for themselves.</span>
          </span>
          <button
            type="button"
            role="switch"
            class="fts-settings-switch"
            :aria-checked="String(access.restrictSharedCalendar)"
            aria-label="Only admins add a calendar for everyone"
            :disabled="saving"
            @click="toggle('restrictSharedCalendar')"
          >
            <span></span>
          </button>
        </div>

        <span class="fts-settings-note">Saved as you change it.</span>
      </template>
    </section>
  </div>
</template>

<script>
/**
 * Settings › Security (alpha, P0 slice).
 *
 * The "everyone views" policy (SS03547) moves out of the dialog's Access tab and saves
 * on change instead of behind a Save button. Joining (lobby + invite link) and the
 * two-step switch follow once their read endpoints exist.
 */
import { store as canvasDesignStore } from "../../store/canvasDesignStore";
import FTSSpinner from "../../elements/FTSSpinner";

export default {
  name: "SettingsSecurity",
  components: { "fts-spinner": FTSSpinner },
  props: {
    workspaceId: [String, Number],
  },
  data() {
    return {
      loading: true,
      saving: false,
      error: "",
      access: {
        restrictActivityStream: false,
        restrictSharedCalendar: false,
      },
      twoFactor: {
        loading: true,
        saving: false,
        unavailable: "",
        enforced: false,
        total: 0,
        enrolled: 0,
        pending: 0,
      },
    };
  },
  computed: {
    enrolmentLine() {
      const t = this.twoFactor;
      return `${t.enrolled} of ${t.total} ${t.total === 1 ? "person has" : "people have"} set it up`;
    },
    enrolmentPct() {
      const t = this.twoFactor;
      if (!t.total) return "0%";
      return `${Math.round((t.enrolled / t.total) * 100)}%`;
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const res = await canvasDesignStore.dispatch("retrieveEveryoneAccess", this.workspaceId);
        const data = (res && res.data) || {};
        this.access = {
          restrictActivityStream: Boolean(data.restrictActivityStream),
          restrictSharedCalendar: Boolean(data.restrictSharedCalendar),
        };
      } catch (err) {
        this.error = this.message(err, "These settings are not available right now.");
      } finally {
        this.loading = false;
      }
    },
    async toggle(key) {
      if (this.saving) return;
      const before = { ...this.access };
      this.access = { ...this.access, [key]: !this.access[key] };
      this.saving = true;
      try {
        await canvasDesignStore.dispatch("saveEveryoneAccess", {
          workspaceId: this.workspaceId,
          ...this.access,
        });
        this.$toasted.success("Saved");
      } catch (err) {
        this.access = before;
        this.$toasted.global.error(err, this.message(err, "That change was not saved."));
      } finally {
        this.saving = false;
      }
    },
    async loadTwoFactor() {
      this.twoFactor.loading = true;
      this.twoFactor.unavailable = "";
      try {
        const res = await canvasDesignStore.dispatch("retrieveTwoFactorStatus", this.workspaceId);
        const data = (res && res.data) || {};
        this.twoFactor.enforced = Boolean(data.enforceTwoFactor);
        this.twoFactor.total = Number(data.total || 0);
        this.twoFactor.enrolled = Number(data.enrolled || 0);
        this.twoFactor.pending = Number(data.pending || 0);
      } catch (err) {
        const status = err && err.response && err.response.status;
        // 404 = this environment's API is older than the endpoint. Saying so beats a switch
        // that cannot know whether it is on.
        this.twoFactor.unavailable = status === 404
          ? "Set by Inistate for this workspace. The switch appears once this environment runs an API that reports it."
          : this.message(err, "This setting is not available right now.");
      } finally {
        this.twoFactor.loading = false;
      }
    },
    async toggleTwoFactor() {
      if (this.twoFactor.saving) return;
      const before = this.twoFactor.enforced;
      this.twoFactor.enforced = !before;
      this.twoFactor.saving = true;
      try {
        await canvasDesignStore.dispatch("saveEnforceTwoFactor", {
          workspaceId: Number(this.workspaceId),
          enabled: this.twoFactor.enforced,
        });
        this.$toasted.success(this.twoFactor.enforced
          ? "Two-step verification is now required"
          : "Two-step verification is no longer required");
      } catch (err) {
        this.twoFactor.enforced = before;
        this.$toasted.global.error(err, this.message(err, "That change was not saved."));
      } finally {
        this.twoFactor.saving = false;
      }
    },
    message(err, fallback) {
      const data = err && err.response && err.response.data;
      return (data && (data.message || data.error_description)) || fallback;
    },
  },
  watch: {
    workspaceId() {
      this.load();
      this.loadTwoFactor();
    },
  },
  created() {
    this.load();
    this.loadTwoFactor();
  },
};
</script>
